import APIContext from ".";
import {
  usePatientQuery,
  useDispositionsQuery,
  usePatientDispositionsQuery,
  useEDDQuery,
  useConfigurationQuery,
  useIntelligenceTextQuery,
  usePatientMilestonesQuery,
  usePatientClinicalInsightsQuery,
  useMilestonesQuery,
  useMedicallyReadyStatusQuery,
  usePatientInsightsQuery,
  useActiveRoundingSessionQuery,
  usePatientConsiderationsQuery,
  usePatientNotesQuery,
  useUserPreferencesQuery,
} from "../../api/queries";

import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { computeProductionDataReadOnly } from "../../utils/patient-embed-environment";
import { AnalyticsBrowser } from "@segment/analytics-next";
import Configuration from "../../types/Configuration";

interface APIContextProviderProps {
  emrPatientID: string;
  emrHospitalID: string;
  fhirPatientID: string;
  authContextID: string;
}

const APIContextProvider: FC<APIContextProviderProps> = ({
  children,
  emrPatientID,
  emrHospitalID,
  fhirPatientID,
  authContextID,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsBrowser | null>(null);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const patientQuery = usePatientQuery(
    emrPatientID,
    emrHospitalID,
    fhirPatientID
  );

  const configurationQuery = useConfigurationQuery();
  const userPreferencesQuery = useUserPreferencesQuery();

  const configuration: Configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : null;

  const includeHistory = configuration?.update_history_enabled;

  const patientID = patientQuery.data?.id;
  const isViewOnly = patientQuery.data?.is_view_only ?? false;
  const productionDataReadOnly = useMemo(
    () => computeProductionDataReadOnly(userPreferencesQuery),
    [userPreferencesQuery]
  );
  const patientDataReadOnly = isViewOnly || productionDataReadOnly;

  const patientDispositionsQuery = usePatientDispositionsQuery(
    patientID,
    includeHistory
  );
  const eddQuery = useEDDQuery(patientID, includeHistory);
  const patientMilestonesQuery = usePatientMilestonesQuery(patientID);
  const patientClinicalInsightsQuery =
    usePatientClinicalInsightsQuery(patientID);
  const intelligenceTextQuery = useIntelligenceTextQuery(patientID);
  const medicallyReadyStatusQuery = useMedicallyReadyStatusQuery(patientID);
  const patientInsightsQuery = usePatientInsightsQuery(patientID);
  const activeRoundingSessionQuery = useActiveRoundingSessionQuery();
  const patientConsiderationsQuery = usePatientConsiderationsQuery(patientID);
  const patientNotesQuery = usePatientNotesQuery(patientID);

  const dispositionsQuery = useDispositionsQuery(patientQuery.isSuccess);
  const milestonesQuery = useMilestonesQuery(patientQuery.isSuccess);

  useEffect(() => {
    if (!configurationQuery.isSuccess) return;
    const appSettings = configurationQuery.data?.[0]?.app_settings;
    if (!appSettings?.fe_segment_write_key) return;
    const analyticsInit = AnalyticsBrowser.load({
      writeKey: appSettings.fe_segment_write_key,
    });
    const urlParams = new URLSearchParams(window.location.search);
    analyticsInit.identify(urlParams.get("emr_user_id"), {
      username: urlParams.get("emr_user_full_name"),
      email: urlParams.get("emr_user_email"),
      role: urlParams.get("emr_user_role"),
      service_area: urlParams.get("emr_user_service_area"),
      dept_speciality: urlParams.get("emr_user_dept_speciality"),
      stack: window.location.host.split(".")[0],
    });
    setAnalytics(analyticsInit);
  }, [configurationQuery.isSuccess, configurationQuery.data]);

  // Since events from EBA can fire anytime and update many fields, we use the patient dispositions query to gate the refresh
  // this will cascade across and update everything as needed.
  // Skip the first poll so freshly-seeded cache (from the patient list) isn't immediately invalidated.
  const isFirstPoll = useRef(true);
  useEffect(
    () => {
      if (
        patientDispositionsQuery.isSuccess &&
        !patientDispositionsQuery.isFetching
      ) {
        if (isFirstPoll.current) {
          isFirstPoll.current = false;
          return;
        }
        eddQuery.refetch();
        dispositionsQuery.refetch();
        patientMilestonesQuery.refetch();
        patientClinicalInsightsQuery.refetch();
        intelligenceTextQuery.refetch();
        medicallyReadyStatusQuery.refetch();
        patientConsiderationsQuery.refetch();
      }
    },
    // eslint-disable-next-line
    [patientDispositionsQuery.isFetching, patientDispositionsQuery.isSuccess]
  );

  useEffect(() => {
    // Check for JWT token expired error in patientDispositionsQuery
    if (
      patientDispositionsQuery.error &&
      (patientDispositionsQuery.error as any)?.response?.status === 403 &&
      (patientDispositionsQuery.error as any)?.response?.data?.detail?.includes(
        "JWT token has expired"
      )
    ) {
      setSessionExpired(true);
    }
  }, [patientDispositionsQuery.error]);

  return (
    <APIContext.Provider
      value={{
        patientID,
        patientQuery,
        dispositionsQuery,
        patientDispositionsQuery,
        eddQuery,
        milestonesQuery,
        patientMilestonesQuery,
        patientClinicalInsightsQuery,
        configurationQuery,
        userPreferencesQuery,
        authContextID,
        intelligenceTextQuery,
        medicallyReadyStatusQuery,
        patientInsightsQuery,
        activeRoundingSessionQuery,
        patientConsiderationsQuery,
        patientNotesQuery,
        analytics,
        showErrorToast,
        setShowErrorToast,
        sessionExpired,
        isViewOnly,
        productionDataReadOnly,
        patientDataReadOnly,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};

export default APIContextProvider;
